/**
 * Aircraft marker colour by altitude — port of tar1090's `ColorByAlt`
 * (https://github.com/wiedehopf/tar1090). HSL space; hue varies by altitude
 * in feet, saturation constant at 88, lightness varies per hue band.
 *
 * Stops verified against `tar1090/html/defaults.js`:
 *   0 ft  → hue 20  (orange)
 *   2000  → 32.5    (yellow-orange)
 *   4000  → 43
 *   6000  → 54      (yellow)
 *   8000  → 72
 *   9000  → 85
 *   11000 → 140     (light green)
 *   40000 → 300     (magenta)
 *   51000 → 360     (red)
 *
 * Special states:
 *   on-ground (alt = "ground" or null) → dark grey-blue
 *   unknown altitude                   → light grey
 *
 * Selected aircraft: saturate + lighten slightly. Stale (no update > 30s):
 * desaturate + lighten so it visually fades without losing identity.
 */

interface Stop { ft: number; h: number; l: number }

// Hue stops (altitude in ft → hue in degrees).
const HUE_STOPS: Stop[] = [
  { ft: 0,     h: 20,   l: 50 },
  { ft: 2000,  h: 32.5, l: 54 },
  { ft: 4000,  h: 43,   l: 52 },
  { ft: 6000,  h: 54,   l: 51 },
  { ft: 8000,  h: 72,   l: 46 },
  { ft: 9000,  h: 85,   l: 43 },
  { ft: 11000, h: 140,  l: 41 },
  { ft: 40000, h: 300,  l: 43 },
  { ft: 51000, h: 360,  l: 53 },
];

const SAT = 88;
const ON_GROUND = 'hsl(220, 0%, 30%)';
const UNKNOWN_ALT = 'hsl(0, 0%, 75%)';

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Linear interpolation between the surrounding stops. Below the first
 *  stop clamps to 0; above the last clamps to 51000. */
function interpolateHL(altFt: number): { h: number; l: number } {
  if (altFt <= HUE_STOPS[0]!.ft) return { h: HUE_STOPS[0]!.h, l: HUE_STOPS[0]!.l };
  const last = HUE_STOPS[HUE_STOPS.length - 1]!;
  if (altFt >= last.ft) return { h: last.h, l: last.l };
  for (let i = 1; i < HUE_STOPS.length; i++) {
    const a = HUE_STOPS[i - 1]!;
    const b = HUE_STOPS[i]!;
    if (altFt <= b.ft) {
      const t = (altFt - a.ft) / (b.ft - a.ft);
      return { h: lerp(a.h, b.h, t), l: lerp(a.l, b.l, t) };
    }
  }
  return { h: last.h, l: last.l };
}

export interface AltitudeColorOptions {
  /** Mark selected aircraft brighter so they pop out of the gradient. */
  selected?: boolean;
  /** Stale = no fresh update for >30 s. Desaturated, lightened. */
  stale?: boolean;
  /** MLAT-only aircraft can be tinted differently if you want — defaults to
   *  the regular gradient because MLAT is still a valid position fix. */
  mlat?: boolean;
}

/** Return a CSS `hsl(...)` string given an altitude in feet. */
export function altitudeToColor(
  altFt: number | null | undefined,
  opts: AltitudeColorOptions = {},
): string {
  if (altFt == null) return UNKNOWN_ALT;
  if (altFt <= 0) return ON_GROUND;
  let { h, l } = interpolateHL(altFt);
  let s = SAT;
  if (opts.stale)    { s = Math.max(0, s - 35); l = Math.min(100, l + 9); }
  if (opts.selected) { s = Math.min(100, s + 10); l = Math.min(100, l + 5); }
  return `hsl(${h.toFixed(1)}, ${s}%, ${l}%)`;
}

/** Helper for callers that have both alt_geom (preferred — GPS) and
 *  alt_baro (fallback — pressure-corrected). */
export function pickAltitude(altGeom: number | null, altBaro: number | null): number | null {
  if (altGeom != null && Number.isFinite(altGeom)) return altGeom;
  if (altBaro != null && Number.isFinite(altBaro)) return altBaro;
  return null;
}
